<template>
  <header class="app-topbar">
    <!-- Left: Hamburger (mobile) + Breadcrumb -->
    <div class="app-topbar__left">
      <button
        v-if="isMobile"
        class="app-topbar__hamburger"
        @click="$emit('toggle-sidebar')"
        aria-label="Open navigation menu"
      >
        ☰
      </button>
      <nav class="app-topbar__breadcrumb" aria-label="Breadcrumb">
        <router-link v-if="currentView !== 'home'" :to="{ name: 'home' }">Home</router-link>
        <span v-else>Home</span>
        <span v-if="currentView !== 'home'"> &gt; </span>
        <span v-if="currentView === 'import'">Import Services</span>
        <span v-else-if="currentView === 'compare'">{{ previousView }} &gt; Compare</span>
        <span v-else-if="currentView !== 'home'">{{ currentView }}</span>
      </nav>
    </div>

    <!-- Center: App title -->
    <h1 class="app-topbar__title">{{ appTitle }}</h1>

    <!-- Right: Controls -->
    <div class="app-topbar__right">
      <span class="app-topbar__user">{{ user?.email }} ({{ displayGroups }})</span>
      <EnvironmentSelector :disabled="currentView === 'compare'" />
      <div class="app-topbar__compare">
        <label for="compare-select">Compare:</label>
        <select
          id="compare-select"
          :value="compareEnvironment"
          @change="handleCompareChange"
          :disabled="!canCompare && currentView !== 'compare'"
        >
          <option value="">None</option>
          <option v-for="env in availableEnvironments" :key="env" :value="env">
            {{ env.toUpperCase() }}
          </option>
        </select>
        <button
          v-if="currentView === 'compare'"
          @click="$emit('close-comparison')"
          class="btn-primary"
        >
          Back to {{ previousView }}
        </button>
      </div>
      <ThemeToggle />
      <button @click="$emit('sign-out')" class="btn-danger">Sign Out</button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useEnvironmentStore } from '../../stores/environment';
import { getAccessibleEnvironments } from '../../utils/environmentConfig';
import EnvironmentSelector from '../EnvironmentSelector.vue';
import ThemeToggle from '../ThemeToggle.vue';

const props = defineProps<{
  appTitle: string;
  user: { email: string; username: string } | null;
  userGroups: string[];
  currentView: string;
  previousView: string;
  compareEnvironment: string;
  canCompare: boolean;
  isMobile: boolean;
}>();

const emit = defineEmits<{
  (e: 'sign-out'): void;
  (e: 'compare-change', env: string): void;
  (e: 'close-comparison'): void;
  (e: 'toggle-sidebar'): void;
}>();

const environmentStore = useEnvironmentStore();

const displayGroups = computed(() => {
  const recognizedGroups = ['readonly', 'developer', 'deployment', 'admin'];
  const filtered = props.userGroups.filter(group => recognizedGroups.includes(group));
  return filtered.join(', ');
});

const availableEnvironments = computed(() => {
  const accessibleEnvs = getAccessibleEnvironments(props.userGroups);
  return accessibleEnvs
    .map(env => env.value)
    .filter(env => env !== environmentStore.currentEnvironment);
});

const handleCompareChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  emit('compare-change', target.value);
};
</script>

<style scoped>
.app-topbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  background: var(--color-surface);
  border-bottom: 1px solid var(--border-color);
  height: 56px;
  max-height: 60px;
  overflow: hidden;
}

.app-topbar__left {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.app-topbar__hamburger {
  background: none;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: var(--space-1) var(--space-2);
  cursor: pointer;
  font-size: var(--text-lg);
  color: var(--color-text-primary);
}

.app-topbar__hamburger:hover {
  background: var(--color-surface-raised);
}

.app-topbar__breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.app-topbar__breadcrumb a {
  color: var(--color-primary);
  text-decoration: none;
}

.app-topbar__breadcrumb a:hover {
  text-decoration: underline;
}

.app-topbar__title {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  margin: 0;
  white-space: nowrap;
}

.app-topbar__right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.app-topbar__user {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.app-topbar__compare {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  white-space: nowrap;
}

.app-topbar__compare label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
}

.app-topbar__compare select {
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--input-bg);
  color: var(--text-color);
  font-size: var(--text-sm);
}

.app-topbar__compare select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Task 4.3: Responsive styling */
@media (max-width: 767px) {
  .app-topbar__title {
    display: none;
  }

  .app-topbar__user {
    display: none;
  }

  .app-topbar__right {
    gap: var(--space-1);
  }
}
</style>
